import * as React from "react"
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  EmptyState,
  Spacer,
  useShowSuccessToast,
} from "gatsby-interface"

import { envVars as text, ui as uiText } from "@modules/locales/default.js"
import Loading from "@modules/ui/components/Loading"
import { BuildRunnerType, Tag } from "@modules/graphql/types"
import { useAllEnvironmentVariablesForSiteQuery } from "@modules/site/envVars/queries.generated"
import { EnvVarsDisplay } from "@modules/site/envVars/components/EnvVarsDisplay"
import {
  EnvironmentVariablesForm,
  EnvVarsFieldArray,
} from "./EnvironmentVariablesForm"
import { useUpdateAllEnvironmentVariablesForSite } from "./SiteEnvironmentVariables.helpers"
import { MdArrowForward } from "react-icons/md"
import { interpolateMessage } from "@modules/locales"
import { EnvVarsUiSource } from "../types"
import { useTracker } from "@modules/analytics"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"

const defaultRenderActions = (actions: React.ReactNode) => actions

export type SiteEnvironmentVariablesMode = `VIEW` | `EDIT`

export type SiteEnvironmentVariablesProps = {
  organizationId: string
  siteId: string
  uiSource: EnvVarsUiSource
  renderActions?: (actions: React.ReactNode) => React.ReactNode
  siteBuildsEnabled?: boolean
  mode: `VIEW` | `EDIT`
  onModeChange: (mode: SiteEnvironmentVariablesMode) => void
}

export function SiteEnvironmentVariables({
  siteId,
  organizationId,
  siteBuildsEnabled = true,
  mode,
  onModeChange,
  renderActions = defaultRenderActions,
  uiSource,
}: SiteEnvironmentVariablesProps) {
  const { trackButtonClicked } = useTracker()
  const showSuccessToast = useShowSuccessToast()
  const [
    updateAllEnvironmentVariablesForSite,
    { loading: isSubmitting, errorAlert },
  ] = useUpdateAllEnvironmentVariablesForSite()
  const {
    data,
    error,
    loading,
    refetch,
  } = useAllEnvironmentVariablesForSiteQuery({
    variables: {
      id: siteId,
    },
  })
  const [tabIndex, setTabIndex] = React.useState<number>(0)

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  const buildVariables = data?.buildEnvironmentVariablesForSite || []
  const previewVariables = data?.previewEnvironmentVariablesForSite || []

  const shouldShowForm = mode === `EDIT`

  const previewVariablesContent = (
    <EnvironmentVariablesPanelContent
      siteId={siteId}
      envVars={previewVariables}
      runnerType={BuildRunnerType.Preview}
      fieldName="previewVariables"
      mode={mode}
      uiSource={uiSource}
      loading={loading}
    />
  )

  return (
    <EnvironmentVariablesForm
      onSubmit={values => {
        trackButtonClicked(`Save environment variables`, {
          organizationId,
          siteId,
          uiSource,
        })

        return updateAllEnvironmentVariablesForSite(
          {
            id: siteId,
            buildEnvironmentVariables: values.buildVariables,
            previewEnvironmentVariables: values.previewVariables,
          },
          () => {
            onModeChange(`VIEW`)
            showSuccessToast("Environment variables updated!")
            refetch()
          }
        )
      }}
      onValidationError={({ hasBuildVarsError, hasPreviewVarsError }) => {
        /**
         * Since we're saving both preview and build variables at the same time,
         * we want to activate the other tab if it has errors and the current one does not
         */
        if (hasBuildVarsError && !hasPreviewVarsError) {
          setTabIndex(0)
        } else if (hasPreviewVarsError && !hasBuildVarsError) {
          setTabIndex(1)
        }
      }}
    >
      {siteBuildsEnabled ? (
        <Tabs index={tabIndex} onChange={setTabIndex}>
          <TabList>
            <Tab>{text.labels.buildVariablesTab}</Tab>
            <Tab>{text.labels.previewVariablesTab}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <EnvironmentVariablesPanelContent
                siteId={siteId}
                envVars={buildVariables}
                runnerType={BuildRunnerType.Builds}
                fieldName="buildVariables"
                mode={mode}
                uiSource={uiSource}
                loading={loading}
              />
            </TabPanel>
            <TabPanel>{previewVariablesContent}</TabPanel>
          </TabPanels>
        </Tabs>
      ) : (
        previewVariablesContent
      )}
      <Spacer size={7} />
      {shouldShowForm ? errorAlert : null}
      {shouldShowForm &&
        renderActions(
          <React.Fragment>
            <Button
              size="M"
              variant="SECONDARY"
              tone="NEUTRAL"
              data-cy="cancel-button"
              onClick={() => onModeChange(`VIEW`)}
              type={`reset`}
            >
              {uiText.actions.cancel}
            </Button>
            <Button
              size="M"
              type="submit"
              disabled={isSubmitting}
              data-cy="env-vars-key-save-btn"
              rightIcon={<MdArrowForward />}
            >
              {uiText.actions.save}
            </Button>
          </React.Fragment>
        )}
    </EnvironmentVariablesForm>
  )
}

type EnvironmentVariablesPanelContentProps = {
  siteId: string
  envVars: Tag[]
  runnerType: BuildRunnerType
  fieldName: `buildVariables` | `previewVariables`
  mode: SiteEnvironmentVariablesMode
  uiSource: EnvVarsUiSource
  loading: boolean
}

function EnvironmentVariablesPanelContent({
  siteId,
  envVars,
  runnerType,
  fieldName,
  mode,
  uiSource,
  loading,
}: EnvironmentVariablesPanelContentProps) {
  if (loading) {
    return (
      <Loading
        delay={1000}
        message={text.messages.loadingEnvVars}
        bufferSize="padded"
      />
    )
  }

  if (mode === `EDIT`) {
    return (
      <EnvVarsFieldArray
        name={fieldName}
        siteId={siteId}
        uiSource={uiSource}
        envVars={envVars}
      />
    )
  }

  if (envVars.length === 0) {
    return (
      <EmptyState
        heading={text.headers.noEnvVars}
        text={interpolateMessage<"envVarsOfType">(
          text.messages.noEnvVarsDescription,
          {
            envVarsOfType:
              runnerType === BuildRunnerType.Preview
                ? text.messages.noEnvVarsTypePreview
                : text.messages.noEnvVarsTypeBuild,
          }
        )}
        variant="WITH_BACKGROUND"
      />
    )
  }

  return <EnvVarsDisplay envVars={envVars} />
}
